import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Avatar, Box, CircularProgress, Paper, Typography } from "@mui/material";
import AppChip from "../../common/app-chip";
import BackButton from "../../common/back-button";
import ButtonOne from "../../common/normal-button";
import CustomTabs from "../../common/custom-tabs";
import typography from "../../cssUtils/typography";
import { ColorsConstants, borderColors, grayFontColor } from "../../cssUtils/constants";
import { MEMBERS_BULK_ENDPOINT, MEMBERS_ENDPOINT } from "../../config/urls";
import { ApiError, fieldErrorsFrom, messageFrom, useMembershipClasses } from "./membersApi";
import MemberForm from "./MemberForm";
import {
  describeActivity,
  formatDate,
  formatDateTime,
  initialsOf,
  memberStatus,
  memberToFormValues,
  syncStatusChip,
  toPayload,
  validateMember,
} from "./membersUtils";

const DETAIL_TABS = [
  { value: "profile", label: "Profile" },
  { value: "membership", label: "Membership" },
  { value: "activity", label: "Activity" },
];

// Edit form layout; Membership ID is shown but cannot be changed.
const EDIT_SECTIONS = [
  { title: "Contact", fields: ["mobile", "office_phone", "residence_phone", "email"] },
  { title: "Personal", fields: ["salutation", "full_name", "dob", "anniversary"] },
  {
    title: "Address",
    fields: ["address_type", "firm_name", "address_line1", "address_line2", "area", "district", "city", "state", "country", "pin_code"],
  },
  { title: "Membership", fields: ["membership_id", "membership_class", "registration_date", "membership_end_date", "services_to_date", "gstin"] },
  { title: "ICAI", fields: ["icai_no", "icai_date"] },
];

const dash = (value) => (value === undefined || value === null || value === "" ? "—" : value);

const fieldOrder = EDIT_SECTIONS.flatMap((section) => section.fields);

const readGroups = (member, tab) => {
  if (tab === "profile") {
    return [
      { title: "Contact", rows: [["Mobile", member.mobile], ["Office phone", member.office_phone], ["Residence phone", member.residence_phone], ["Email", member.email]] },
      { title: "Personal", rows: [["Salutation", member.salutation], ["Full name", member.full_name], ["Date of birth", member.dob && formatDate(member.dob)], ["Anniversary", member.anniversary && formatDate(member.anniversary)]] },
      {
        title: "Address",
        rows: [
          ["Address type", member.address_type], ["Firm name", member.firm_name], ["Line 1", member.address_line1],
          ["Line 2", member.address_line2], ["Area", member.area], ["District", member.district], ["City", member.city],
          ["State", member.state], ["Country", member.country], ["Pin code", member.pin_code],
        ],
      },
      { title: "ICAI", rows: [["ICAI no.", member.icai_no], ["ICAI date", member.icai_date && formatDate(member.icai_date)]] },
    ];
  }
  return [
    {
      title: "Membership",
      rows: [
        ["Membership ID", member.membership_id],
        ["Class", member.membership_class_name],
        ["Type", member.is_life ? "Life (no renewal)" : "Annual"],
        ["Status", memberStatus(member).label],
        ["Member since", member.member_since && formatDate(member.member_since)],
        ["Valid till", member.valid_till ? formatDate(member.valid_till) : "Life"],
      ],
    },
    {
      title: "Services & links",
      rows: [
        ["Services till", member.services_to_date && formatDate(member.services_to_date)],
        ["Linked membership", member.linked_member_name],
        ["GSTIN", member.gstin],
      ],
    },
  ];
};

const MemberDetail = ({ api, memberId, notify, onBack, onChanged }) => {
  const [member, setMember] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [values, setValues] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [activity, setActivity] = useState({ results: [], next: null });
  const [activityPage, setActivityPage] = useState(1);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityTick, setActivityTick] = useState(0);
  const fieldRefs = useRef({});
  const classes = useMembershipClasses(api);

  const load = useCallback(async () => {
    try {
      setMember(await api.request(`${MEMBERS_ENDPOINT}${memberId}/`));
      setLoadError("");
    } catch (error) {
      setLoadError(error.status === 404 ? "This member could not be found." : messageFrom(error, "We couldn't load this member."));
    }
  }, [api, memberId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab !== "activity") return undefined;
    let active = true;
    setActivityLoading(true);
    api
      .request(`${MEMBERS_ENDPOINT}${memberId}/activity/?page=${activityPage}`)
      .then((result) => {
        if (!active) return;
        setActivity((prev) => ({
          results: activityPage === 1 ? result.results : [...prev.results, ...result.results],
          next: result.next,
        }));
      })
      .catch(() => {})
      .finally(() => active && setActivityLoading(false));
    return () => {
      active = false;
    };
  }, [api, memberId, tab, activityPage, activityTick]);

  const refreshAll = async () => {
    await load();
    setActivityPage(1);
    setActivityTick((tick) => tick + 1);
    onChanged?.();
  };

  const bulkOnThis = async (action, successText) => {
    setActing(true);
    try {
      await api.request(MEMBERS_BULK_ENDPOINT, { method: "POST", body: { action, ids: [memberId] } });
      notify(successText);
      await refreshAll();
    } catch (error) {
      notify(messageFrom(error), { severity: "error" });
    } finally {
      setActing(false);
    }
  };

  const startEdit = () => {
    const start = memberToFormValues(member);
    setInitialValues(start);
    setValues(start);
    setErrors({});
    setFormError("");
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const setValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const focusFirst = (errorMap) => {
    const first = fieldOrder.find((key) => errorMap[key]);
    if (first) fieldRefs.current[first]?.focus();
  };

  // Save runs the same validation as the upload; a saved edit triggers an Audience sync server-side.
  const save = async () => {
    const found = validateMember(values, { mode: "edit" });
    setErrors(found);
    setFormError("");
    if (Object.keys(found).length) {
      focusFirst(found);
      return;
    }
    const payload = toPayload(values, initialValues);
    if (!Object.keys(payload).length) {
      setEditing(false);
      notify("No changes to save", { severity: "info" });
      return;
    }
    setSaving(true);
    try {
      setMember(await api.request(`${MEMBERS_ENDPOINT}${memberId}/`, { method: "PATCH", body: payload }));
      setEditing(false);
      notify("Changes saved");
      setActivityPage(1);
      setActivityTick((tick) => tick + 1);
      onChanged?.();
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fromServer = fieldErrorsFrom(error);
        delete fromServer.existing_member_id;
        const known = Object.fromEntries(Object.entries(fromServer).filter(([key]) => key in values));
        setErrors(known);
        focusFirst(known);
        if (!Object.keys(known).length) setFormError(messageFrom(error));
      } else {
        setFormError(messageFrom(error));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
        <BackButton onClick={onBack} />
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }
  if (!member) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress aria-label="Loading member" />
      </Box>
    );
  }

  const status = memberStatus(member);
  const sync = member.audience_sync || { status: "pending" };
  const syncChip = syncStatusChip(sync.status);
  const name = `${member.salutation || ""} ${member.full_name || ""}`.trim();
  const issues = member.data_issues || [];
  const isInactive = member.status === "inactive";

  const glance = [
    ["Mobile / WhatsApp", member.mobile],
    ["Email", member.email],
    ["City", [member.city, member.country].filter(Boolean).join(", ")],
    ["Member since", member.member_since && formatDate(member.member_since)],
    ["Valid till", member.valid_till ? formatDate(member.valid_till) : "Life membership"],
    ["ICAI no.", member.icai_no],
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <BackButton onClick={onBack} />
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              // Light background with the dark brand colour for the initials: both are set
              // explicitly so the contrast does not depend on the theme's default avatar text colour.
              ...typography.h2,
              bgcolor: ColorsConstants.primaryBlue70,
              color: ColorsConstants.primaryBlue100,
              width: 56,
              height: 56,
            }}
          >
            {initialsOf(member.full_name)}
          </Avatar>
          <Box>
            <Typography component="h1" sx={{ ...typography.h2, color: ColorsConstants.primaryBlue100 }}>
              {name || "No name"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
                {member.membership_id || "No Membership ID"}
              </Typography>
              {member.membership_class_name && <AppChip label={member.membership_class_name} variant="outlined" />}
              <AppChip label={status.label} color={status.color} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {editing ? (
            <>
              <ButtonOne variant="outlined" onClick={cancelEdit} disabled={saving}>
                Cancel
              </ButtonOne>
              <ButtonOne onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </ButtonOne>
            </>
          ) : (
            <>
              <ButtonOne
                variant="outlined"
                disabled={acting}
                onClick={() =>
                  isInactive
                    ? bulkOnThis("reactivate", "Member reactivated")
                    : bulkOnThis("deactivate", "Member marked inactive")
                }
              >
                {isInactive ? "Reactivate" : "Mark inactive"}
              </ButtonOne>
              <ButtonOne onClick={startEdit}>Edit details</ButtonOne>
            </>
          )}
        </Box>
      </Box>

      {issues.length > 0 && (
        <Alert severity="warning" role="status">
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>Needs attention</Typography>
          {issues.map((issue, index) => (
            <Typography key={`${issue.code}-${index}`} sx={typography.bodyLarge}>
              • {issue.message}
            </Typography>
          ))}
        </Alert>
      )}
      {formError && (
        <Alert severity="error" role="alert">
          {formError}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" } }}>
        {/* At a glance */}
        <Paper
          component="aside"
          aria-label="At a glance"
          variant="outlined"
          sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0, p: 2.5, borderColor: borderColors.cardBorderPrimaryGrayColor, borderRadius: "10px" }}
        >
          <Typography component="h2" sx={{ ...typography.bodyLarge, fontWeight: 700, mb: 1.5 }}>
            At a glance
          </Typography>
          {glance.map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.75 }}>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{label}</Typography>
              <Typography sx={{ ...typography.bodyLarge, textAlign: "right", wordBreak: "break-word" }}>{dash(value)}</Typography>
            </Box>
          ))}
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>Audience sync</Typography>
              <AppChip label={syncChip.label} color={syncChip.color} />
            </Box>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
              {sync.last_synced_at ? `Last synced ${formatDateTime(sync.last_synced_at)}` : "Not synced yet"}
            </Typography>
            {sync.status === "failed" && sync.last_error && (
              <Typography role="alert" sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 0.5 }}>
                {sync.last_error}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Record */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomTabs value={tab} tabs={DETAIL_TABS} onChange={(event, value) => setTab(value)} />

          {editing ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderColor: borderColors.cardBorderPrimaryGrayColor, borderRadius: "10px" }}>
              <MemberForm
                values={values}
                errors={errors}
                sections={EDIT_SECTIONS}
                classes={classes}
                readOnlyKeys={["membership_id"]}
                onChange={setValue}
                onBlurField={(key) => setErrors((prev) => ({ ...prev, [key]: validateMember(values, { mode: "edit" })[key] || "" }))}
                fieldRefs={fieldRefs}
              />
            </Paper>
          ) : tab === "activity" ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderColor: borderColors.cardBorderPrimaryGrayColor, borderRadius: "10px" }}>
              {activity.results.length === 0 && !activityLoading && (
                <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>No activity yet.</Typography>
              )}
              <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {activity.results.map((entry) => (
                  <Box component="li" key={entry.id} sx={{ display: "flex", gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, mt: 0.9, borderRadius: "50%", backgroundColor: ColorsConstants.primaryBlue90, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={typography.bodyLarge}>{describeActivity(entry)}</Typography>
                      <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                        {formatDateTime(entry.created_at)}
                        {entry.actor_name ? ` · ${entry.actor_name}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {activityLoading && <CircularProgress size={20} sx={{ mt: 1 }} aria-label="Loading activity" />}
              {activity.next && !activityLoading && (
                <ButtonOne variant="outlined" sx={{ mt: 2 }} onClick={() => setActivityPage((page) => page + 1)}>
                  Load more
                </ButtonOne>
              )}
            </Paper>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
              {readGroups(member, tab).map((group) => (
                <Paper key={group.title} variant="outlined" sx={{ p: 2.5, borderColor: borderColors.cardBorderPrimaryGrayColor, borderRadius: "10px" }}>
                  <Typography component="h3" sx={{ ...typography.bodyLarge, fontWeight: 700, mb: 1 }}>
                    {group.title}
                  </Typography>
                  <Box component="dl" sx={{ m: 0 }}>
                    {group.rows.map(([label, value]) => (
                      <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
                        <Typography component="dt" sx={{ ...typography.bodySmall, color: grayFontColor }}>
                          {label}
                        </Typography>
                        <Typography component="dd" sx={{ ...typography.bodyLarge, m: 0, textAlign: "right", wordBreak: "break-word" }}>
                          {dash(value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MemberDetail;
